import { CommMessageType, MessageType, SignatureByte, SotiAssistMessageType } from "./definitions";
import { BaseCommand } from "./commands/baseCommand";
import { InitCommand, InitStreamingCommand, LoginCommand, LoginStreamingCommand, PauseCommand, PongCommand, StopCommand, SysInfoCommand } from "./commands/sessionCommands";
import { ScreenCommand, StreamingCommand } from "./commands/screenCommand";
import { BaseMessage, PocketComm, RCPacketOffsets } from "./messages";
import * as fileCommands from "./commands/fileCommands";
import { KillWindowsProcessCommand, TaskDetailsAndroidCommand, TaskDetailsWindowsCommand, TaskListWindowsCommand, ThreadTimesWindowsCommand } from "./commands/processCommands";
import { AgentDumpLogCommand, AgentRecordingCommand, AnnotationCommand, MultiViewCommand } from "./commands/dispatchCommands";
import * as xsightCommands from "./commands/xsightCommands";
import { Device2DeskCommand } from "./commands/device2DeskCommand";
import { Injector } from "@angular/core";

interface ICommandMap {
    loadMap(): void,
    createCommandInstance(data: Uint8Array): BaseCommand<BaseMessage>
};

// The PocketComCommandMap contains constructors for both standard commands constructed with no params,
// and BaseRequestMessage commands that need the RequestReplyService injected.
type ctorStdCmd = new () => BaseCommand<BaseMessage>;
type ctorInjCtxCmd = new (injector: Injector) => BaseCommand<BaseMessage>;

// The latter command classes need to be decorated with @InjectionContextCommand.
// The absence of the decorator indicates that the command class does not need the injection context
export function InjectionContextCommand<T extends { new(...args: any[]): BaseCommand<BaseMessage> }>(constructor: T) {
    return class extends constructor {
        constructor(...args: any[]) {
            super(args[0]);
        }
    };
}

class PocketComCommandMap implements ICommandMap {
    private map: Map<MessageType, ctorStdCmd | ctorInjCtxCmd>;
    private _isStreaming: boolean;
    constructor(private injector: Injector, isStreaming: boolean) {
        this._isStreaming = isStreaming;
        this.loadMap();
        this.updateCommand(isStreaming);
    }

    loadMap() {
        this.map = new Map<MessageType, ctorStdCmd | ctorInjCtxCmd>([
            [MessageType.PC_DELTASCREEN, ScreenCommand],
            [MessageType.PC_FULLSCREEN, ScreenCommand],
            [MessageType.PC_PONG, PongCommand], // nothing to do
            [MessageType.PC_INIT, InitCommand],
            [MessageType.PC_LOGIN, LoginCommand],
            [MessageType.PC_RCPAUSE, PauseCommand],
            [MessageType.PC_QUERY_DEVICE, fileCommands.QueryDeviceCommand],
            [MessageType.PC_DOS_DIR, fileCommands.GetDirCommand],
            [MessageType.PC_DOS_SEARCH, fileCommands.FileSearchCommand],
            [MessageType.PC_DOS_GET_FILE, fileCommands.GetFileCommand],
            [MessageType.PC_DOS_DEL, fileCommands.DeleteFileCommand],
            [MessageType.PC_DOS_COMPRESSION, fileCommands.CompressItemCommand],
            [MessageType.PC_DOS_RMDIR, fileCommands.DeleteFolderCommand],
            [MessageType.PC_DOS_EMPTY_FOLDER, fileCommands.EmptyFolderCommand],
            [MessageType.PC_DOS_GET_FILE_DATA, fileCommands.GetFileDataCommand],
            [MessageType.PC_DOS_MKDIR, fileCommands.MakeDirectoryCommand],
            [MessageType.PC_DOS_RENAME, fileCommands.RenameFileCommand],
            [MessageType.PC_DOS_SEND_FILE, fileCommands.SendFileCommand],
            [MessageType.PC_DOS_SEND_FILE_DATA, fileCommands.SendFileDataCommand],
            [MessageType.PC_ANDROID_ACTIVITY_MANAGEMENT, TaskDetailsAndroidCommand],
            [MessageType.PC_AGENT_DUMP_LOG, AgentDumpLogCommand],
            [MessageType.PC_AGENT_RECORD, AgentRecordingCommand],
            [MessageType.PC_ANNOTATION, AnnotationCommand],
            [MessageType.PC_STOP, StopCommand],
            [MessageType.PC_GETSYSINFO, SysInfoCommand],
            [MessageType.PC_ADMIN, MultiViewCommand], // no support for multiview yet
            [MessageType.PC_GETTSKLIST, TaskListWindowsCommand],
            [MessageType.PC_GETTHRANDMODLISTS, TaskDetailsWindowsCommand],
            [MessageType.PC_GETTHREADTIMES, ThreadTimesWindowsCommand],
            [MessageType.PC_DEVICE2DESK_NOTIFICATION, Device2DeskCommand],
            [MessageType.PC_KILLTSK, KillWindowsProcessCommand], // nothing to do with this confirmation, currently
        ]);
    }

    // Factory stub to deserialize instances of the appropriate derived class based on the value of MessageType enum found in the IMessageHeader
    createCommandInstance(data: Uint8Array): BaseCommand<BaseMessage> {
        const messageCmd = data[RCPacketOffsets.COMMAND_POSITION];
        const CommandClass = this.map.get(messageCmd);
        if (CommandClass) {
            // pass the RequestReplyService to the commands that require the RequestReplyService for state/notification,
            // the rest will use a constructor w/o params and will not accept the param
            const command = new CommandClass(this.injector);
            command.message.deserialize(data);
            return command;
        } else if (messageCmd === MessageType.MOUSE) {
            // after the video recording session ends, the front end might get
            // some MOUSE messages. There is no valid command and they should be ignored.
            // This is likely a DS streaming code bug when the connection to the RC service
            // ends and messages are erroneously forwarded to the front end
            return undefined;
        } else {
            throw new Error(`Unknown message type for cmd=${messageCmd}`);
        }
    }

    /* istanbul ignore next */
    createCommandInstanceSwitch(data: Uint8Array): BaseCommand<BaseMessage> {
        const messageCmd = data[RCPacketOffsets.COMMAND_POSITION];
        let command = null;
        switch (messageCmd) {
            case MessageType.PC_DELTASCREEN: command = this._isStreaming ? new StreamingCommand(this.injector) : new ScreenCommand(this.injector); break;
            case MessageType.PC_FULLSCREEN: command = this._isStreaming ? new StreamingCommand(this.injector) : new ScreenCommand(this.injector); break;
            case MessageType.PC_PONG: command = new PocketComm.PongMessage(); break; // nothing to do
            case MessageType.PC_INIT: command = this._isStreaming ? new InitStreamingCommand() : new InitCommand(); break;
            case MessageType.PC_LOGIN: command = this._isStreaming ? new LoginStreamingCommand : new LoginCommand(); break;
            case MessageType.PC_RCPAUSE: command = new PauseCommand(); break;
            case MessageType.PC_QUERY_DEVICE: command = new fileCommands.QueryDeviceCommand(this.injector); break;
            case MessageType.PC_DOS_DIR: command = new fileCommands.GetDirCommand(this.injector); break;
            case MessageType.PC_DOS_SEARCH: command = new fileCommands.FileSearchCommand(this.injector); break;
            case MessageType.PC_DOS_GET_FILE: command = new fileCommands.GetFileCommand(); break;
            case MessageType.PC_DOS_DEL: command = new fileCommands.DeleteFileCommand(this.injector); break;
            case MessageType.PC_DOS_COMPRESSION: command = new fileCommands.CompressItemCommand(this.injector); break;
            case MessageType.PC_DOS_RMDIR: command = new fileCommands.DeleteFolderCommand(this.injector); break;
            case MessageType.PC_DOS_EMPTY_FOLDER: command = new fileCommands.EmptyFolderCommand(this.injector); break;
            case MessageType.PC_DOS_GET_FILE_DATA: command = new fileCommands.GetFileDataCommand(); break;
            case MessageType.PC_DOS_MKDIR: command = new fileCommands.MakeDirectoryCommand(this.injector); break;
            case MessageType.PC_DOS_RENAME: command = new fileCommands.RenameFileCommand(this.injector); break;
            case MessageType.PC_DOS_SEND_FILE: command = new fileCommands.SendFileCommand(this.injector); break;
            case MessageType.PC_DOS_SEND_FILE_DATA: command = new fileCommands.SendFileDataCommand(this.injector); break;
            case MessageType.PC_ANDROID_ACTIVITY_MANAGEMENT: command = new TaskDetailsAndroidCommand(this.injector); break;
            case MessageType.PC_AGENT_DUMP_LOG: command = new AgentDumpLogCommand(this.injector); break;
            case MessageType.PC_AGENT_RECORD: command = new AgentRecordingCommand(this.injector); break;
            case MessageType.PC_ANNOTATION: command = new AnnotationCommand(this.injector); break;
            case MessageType.PC_STOP: command = new StopCommand(); break;
            case MessageType.PC_GETSYSINFO: command = new SysInfoCommand(); break;
            case MessageType.PC_ADMIN: command = new MultiViewCommand(); break; // no support for multiview yet
            case MessageType.PC_GETTSKLIST: command = new TaskListWindowsCommand(this.injector); break;
            case MessageType.PC_GETTHRANDMODLISTS: command = new TaskDetailsWindowsCommand(this.injector); break;
            case MessageType.PC_GETTHREADTIMES: command = new ThreadTimesWindowsCommand(this.injector); break;
            case MessageType.PC_DEVICE2DESK_NOTIFICATION: command = new Device2DeskCommand(); break;
            case MessageType.MOUSE: command = undefined; // Likely a DS bug, front end shouldn't get MOUSE messages
            default: throw new Error('Unknown message type');
        }

        return command;
    }

    public updateCommand(isStreaming) {
        this.map.set(MessageType.PC_INIT, isStreaming ? InitStreamingCommand : InitCommand);
        this.map.set(MessageType.PC_LOGIN, isStreaming ? LoginStreamingCommand : LoginCommand);
        this.map.set(MessageType.PC_FULLSCREEN, isStreaming ? StreamingCommand : ScreenCommand);
        this.map.set(MessageType.PC_DELTASCREEN, isStreaming ? StreamingCommand : ScreenCommand);
    }

}

class XSightCommandMap implements ICommandMap {
    private map: Map<SotiAssistMessageType, ctorStdCmd | ctorInjCtxCmd>;
    constructor(private injector: Injector, isStreaming: boolean) {
        this.loadMap();
    }

    loadMap() {
        this.map = new Map<SotiAssistMessageType, ctorStdCmd | ctorInjCtxCmd>([
            [SotiAssistMessageType.SAC_VIDEO_RECORDING_START, xsightCommands.VideoRecordingStartCommand],
            [SotiAssistMessageType.SAC_VIDEO_RECORDING_STOP, xsightCommands.VideoRecordingStopCommand],
            [SotiAssistMessageType.SAC_VIDEO_RECORDING_CANCEL, xsightCommands.VideoRecordingCancelCommand],
            [SotiAssistMessageType.SAC_VIDEO_FILE_NAME, xsightCommands.VideoFileNameCommand],
            [SotiAssistMessageType.SAC_VIDEO_RECORDING_DOWNLOAD_READY, xsightCommands.VideoRecordingDownloadReadyCommand],
            [SotiAssistMessageType.SAC_DEVICE_LOGS_DOWNLOAD_READY, xsightCommands.DeviceLogsDownloadReadyCommand],
            [SotiAssistMessageType.SAC_DEVICE_CAPTURE_ACTIVITY_CREATED, xsightCommands.DeviceCaptureActivityCreatedCommand],
            [SotiAssistMessageType.SAC_DEVICE_LOGS_REQUEST_COMPLETED, xsightCommands.DeviceLogsRequestCompletedCommand],
            [SotiAssistMessageType.SAC_SERVER_STATUS, xsightCommands.ServerStatusCommand],
            [SotiAssistMessageType.SAC_GET_VIDEO_DOWNLOAD_LINK, xsightCommands.VideoDownloadLinkCommand],  // deprecated
        ]);
    }
    createCommandInstance(data: Uint8Array): BaseCommand<BaseMessage> {
        const messageCmd = data[RCPacketOffsets.COMMAND_POSITION];
        const CommandClass = this.map.get(messageCmd);
        if (CommandClass) {
            // pass the RequestReplyService to the commands that require the RequestReplyService for state/notification,
            // the rest will use a constructor w/o params and will not accept the param
            const command = new CommandClass(this.injector);
            command.message.deserialize(data);
            return command;
        } else {
            throw new Error('Unknown message type');
        }
    }

    /* istanbul ignore next */
    createCommandInstanceSwitch(data: Uint8Array): BaseCommand<BaseMessage> {
        const messageCmd = data[RCPacketOffsets.COMMAND_POSITION];
        let command = null;
        switch (messageCmd) {
            case SotiAssistMessageType.SAC_VIDEO_RECORDING_START: command = new xsightCommands.VideoRecordingStartCommand(); break;
            case SotiAssistMessageType.SAC_VIDEO_RECORDING_STOP: command = new xsightCommands.VideoRecordingStopCommand(); break;
            case SotiAssistMessageType.SAC_VIDEO_RECORDING_CANCEL: command = new xsightCommands.VideoRecordingCancelCommand(this.injector); break;
            case SotiAssistMessageType.SAC_VIDEO_FILE_NAME: command = new xsightCommands.VideoFileNameCommand(); break;
            case SotiAssistMessageType.SAC_VIDEO_RECORDING_DOWNLOAD_READY: command = new xsightCommands.VideoRecordingDownloadReadyCommand(); break;
            case SotiAssistMessageType.SAC_DEVICE_LOGS_DOWNLOAD_READY: command = new xsightCommands.DeviceLogsDownloadReadyCommand(); break;
            case SotiAssistMessageType.SAC_DEVICE_CAPTURE_ACTIVITY_CREATED: command = new xsightCommands.DeviceCaptureActivityCreatedCommand(); break;
            case SotiAssistMessageType.SAC_DEVICE_LOGS_REQUEST_COMPLETED: command = new xsightCommands.DeviceLogsRequestCompletedCommand(); break;
            case SotiAssistMessageType.SAC_SERVER_STATUS: command = new xsightCommands.ServerStatusCommand(); break;
            case SotiAssistMessageType.SAC_GET_VIDEO_DOWNLOAD_LINK: command = new xsightCommands.VideoDownloadLinkCommand(this.injector); break;
            default: new Error('Unknown message type');
        }

        return command;
    }
}


export class CommandFactory {
    private _isStreaming: boolean;
    private pocketCommMap: PocketComCommandMap;
    private xsightMap: XSightCommandMap;
    constructor(private injector: Injector, isStreaming?: boolean) {
        this._isStreaming = !!isStreaming;
        this.pocketCommMap = new PocketComCommandMap(injector, this._isStreaming);
        this.xsightMap = new XSightCommandMap(injector, this._isStreaming);
    }

    public createCommandInstance(data: Uint8Array): BaseCommand<BaseMessage> {
        let result: BaseCommand<BaseMessage>;
        const sig: number = data[RCPacketOffsets.SIGNATURE];
        switch (sig) {
            case SignatureByte.SignatureRemoteControlMessage:
                result = this.pocketCommMap.createCommandInstance(data);
                break;
            case SignatureByte.SignatureSotiAssistMessage:
                result = this.xsightMap.createCommandInstance(data);
                break;
            case SignatureByte.SignatureStreamingRCMessage:
            case SignatureByte.SignatureCommMessage:
                const cmd = data[RCPacketOffsets.COMMAND_POSITION];
                if (cmd !== CommMessageType.REMOTE_CONTROL_MESSAGE) {
                    throw new Error(`Unknown message command ${cmd} when PC_INIT is expected`);
                }
                if (!this._isStreaming) {
                    data[RCPacketOffsets.SIGNATURE] = SignatureByte.SignatureRemoteControlMessage;
                }
                data[RCPacketOffsets.COMMAND_POSITION] = MessageType.PC_INIT
                result = this.pocketCommMap.createCommandInstance(data);
                break;
            default:
                throw new Error(`Unknown message signature ${data[RCPacketOffsets.SIGNATURE]}`);
        }
        return result;
    }
}
